import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';

const DateField = ({
  id,
  label,
  name,
  value,
  onChange,
  labelClass,
  isDynamic = false,
  placeholder = '',
  required = false,
  format = 'YYYY-MM-DD',
  hint, // ✅ Hint popup support
}) => {
  const [dateValues, setDateValues] = useState(
    isDynamic ? (Array.isArray(value) ? value : []) : value
  );
  const [showHint, setShowHint] = useState(false);
  const hintRef = useRef(null);
  const labelClasses = `${labelClass || ''} mb-0`;

  /* ---------------- SYNC VALUE ---------------- */
  useEffect(() => {
    if (isDynamic) {
      setDateValues(Array.isArray(value) ? value : []);
    } else {
      setDateValues(value);
    }
  }, [value, isDynamic]);

  /* ---------------- CLOSE HINT ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hintRef.current && !hintRef.current.contains(e.target)) {
        setShowHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ---------------- DATE HELPERS ---------------- */
  const convertToNativeDate = (dateString) => {
    const parsedDate = moment(dateString, format, false);
    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
  };

  const convertToCustomFormat = (dateString) => {
    const parsedDate = moment(dateString, 'YYYY-MM-DD', false);
    return parsedDate.isValid() ? parsedDate.format(format) : '';
  };

  /* ---------------- HANDLERS ---------------- */
  const handleDynamicChange = (index, newValue) => {
    const updatedValues = [...dateValues];
    updatedValues[index] = convertToCustomFormat(newValue);
    setDateValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleAddField = () => {
    const updatedValues = [...dateValues, ''];
    setDateValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleRemoveField = (index) => {
    const updatedValues = dateValues.filter((_, idx) => idx !== index);
    setDateValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleStaticChange = (e) => {
    const newValue = convertToCustomFormat(e.target.value);
    onChange(id, newValue);
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="form-group">
      {label && (
        <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
          <label htmlFor={name} className={labelClasses}>
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>

          {hint && (
            <div className="position-relative" ref={hintRef}>
              <span
                role="button"
                tabIndex={0}
                className="text-muted"
                style={{ cursor: 'pointer' }}
                aria-label="Show hint"
                onClick={() => setShowHint((prev) => !prev)}
                onKeyDown={(e) => e.key === 'Enter' && setShowHint((prev) => !prev)}
              >
                ℹ️
              </span>

              {showHint && (
                <div
                  className="position-absolute bg-white border shadow rounded p-2"
                  style={{
                    top: '120%',
                    left: 0,
                    width: '260px',
                    zIndex: 3000,
                  }}
                >
                  <div className="small text-muted">{hint}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isDynamic ? (
        <div>
          {dateValues.length === 0 && (
            <div className="d-flex align-items-center mb-2">
              <input
                type="date"
                className="form-control me-2"
                value=""
                placeholder={placeholder}
                disabled
                required={required}
              />
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleAddField}
              >
                Add
              </button>
            </div>
          )}

          {dateValues.map((date, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <input
                type="date"
                className="form-control me-2"
                value={convertToNativeDate(date)}
                onChange={(e) =>
                  handleDynamicChange(index, e.target.value)
                }
                placeholder={placeholder}
                required={required}
              />
              <button
                type="button"
                className="btn btn-danger btn-sm me-2"
                onClick={() => handleRemoveField(index)}
              >
                Remove
              </button>
              {index === dateValues.length - 1 && (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleAddField}
                >
                  Add
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <input
          type="date"
          id={name}
          name={name}
          className="form-control"
          value={convertToNativeDate(value)}
          onChange={handleStaticChange}
          placeholder={placeholder}
          required={required}
        />
      )}
    </div>
  );
};

export default DateField;
